import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  callId: string
  /** Stream URL for the .ogg download. Pass `null` to hide the download
   *  item (used for `merge_failed` recordings where the file is not
   *  playable). Non-null for `ready` / `partial_sim` / `partial_op` /
   *  `incomplete`. */
  streamUrl: string | null
  onRequestDelete: (callId: string) => void
  /** Optional caller-provided testIds to keep parity with existing
   *  tests that previously found inline buttons. */
  triggerTestId?: string
  downloadTestId?: string
  deleteTestId?: string
}

interface PopPos {
  top: number
  left: number
}

/**
 * Kebab "..." button next to the AudioPlayer that opens a small
 * popover with Скачать + Удалить. Replaces inline action buttons in
 * Call History and /staff/recordings rows so the row stays compact and
 * "Удалить" is one extra click away — accidental clicks are unlikely
 * because the row no longer carries the red-tinted button.
 *
 * Popover renders through a portal with `position: fixed` (anchored
 * to the trigger via getBoundingClientRect) — without this, popovers
 * inside table <td> get clipped by overflow and disappear under the
 * next row. See `.claude/rules/web-layout.md`.
 */
export function RecordingActionsMenu({
  callId,
  streamUrl,
  onRequestDelete,
  triggerTestId,
  downloadTestId,
  deleteTestId,
}: Props) {
  const [pos, setPos] = useState<PopPos | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const open = pos !== null

  useEffect(() => {
    if (!open) return
    const close = (): void => setPos(null)
    const onDoc = (e: MouseEvent): void => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (popoverRef.current?.contains(t)) return
      close()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    // capture: true → catches scrolls in nested scroll containers
    // (table wrappers, modal bodies) where `scroll` doesn't bubble.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const toggle = (): void => {
    if (open) {
      setPos(null)
      return
    }
    const rect = triggerRef.current?.getBoundingClientRect()
    setPos({
      top: rect ? rect.bottom + 4 : 4,
      // Anchor menu's right edge to the button's right edge — on a
      // narrow column it would overflow if anchored left. We position
      // via `left` so test envs without layout still get a number.
      left: rect ? Math.max(8, rect.right - 160) : 8,
    })
  }

  const handleDelete = (): void => {
    setPos(null)
    onRequestDelete(callId)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-label="Действия с записью"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={triggerTestId}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="z-50 min-w-[10rem] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1"
          >
            {streamUrl && (
              <a
                href={streamUrl}
                download={`${callId}.ogg`}
                role="menuitem"
                onClick={() => setPos(null)}
                data-testid={downloadTestId}
                className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Скачать
              </a>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={handleDelete}
              data-testid={deleteTestId}
              className="block w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              Удалить
            </button>
          </div>,
          document.body,
        )}
    </>
  )
}
